import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';
import { Input, InputAdornment, FormControl } from '@mui/material';
import styled from 'styled-components';
import {
  horseHeightInHandsMask,
  weightMask,
} from '../../utils/resources/MaskFunctions';

const StyledInput = styled(Input)`
  min-width: 100%;

  &&&&&&&&&&&& input[type='text'] {
    border: none;
    transition: none;
    padding: 20px 0 20px 16px;
    width: inherit;
    &:active,
    &:focus,
    &:target,
    &:hover {
      border: none;
      box-shadow: none;
    }
  }

  &:before,
  &:after,
  &:hover:not(.Mui-disabled):before {
    border-bottom: none;
    content: none;
    transition: none;
  }
`;

const StyledFormControl = styled(FormControl)`
  min-width: 100%;

  .MuiInputBase-root,
  .MuiOutlinedInput {
    border: 1px solid #888888;
    border-radius: 0;
    font-family: 'Whitney Book';
    transition: none;
    width: 100%;

    &.Mui-focused,
    &:active,
    &:focus,
    &:target,
    &:hover {
      border-color: #006c9c;
      outline: 1px solid #006c9c;
      box-shadow: none;
    }
  }

  .helper-text {
    color: #555;
    font-size: 90%;
    margin-top: 0.25rem;
  }

  .error-message {
    color: #bf1e2d;
    font-family: Whitney Medium;
    font-size: 90%;
    margin: 0.5rem -2rem 0;
    text-align: center;
  }
`;

const StyledInputAdornment = styled(InputAdornment)`
  padding-right: 1rem;
`;

const MaskedNumber = React.forwardRef(function MaskedDate(props, ref) {
  const { id, name, required, onChange, onBlur, value, placeholder } = props;

  const mask = name === 'weight' ? weightMask : horseHeightInHandsMask;

  return (
    <MaskedInput
      id={id}
      name={name}
      value={value}
      inputMode="text"
      type="text"
      required={required}
      onChange={onChange}
      onBlur={onBlur}
      mask={mask}
      placeholder={placeholder}
      keepCharPositions
      guide={false}
    />
  );
});

export const MaskedInputWithUnits = React.forwardRef(
  function MaskedInputWithUnits(props, ref) {
    const {
      id,
      onChange,
      onClick,
      onBlur,
      name,
      value,
      placeholder,
      unit,
      helperText,
      errorMessage,
    } = props;

    const errorRef = useRef(null);

    useEffect(() => {
      if (errorMessage && errorRef.current) {
        errorRef.current.focus();
      }
    }, [errorMessage]);

    return (
      <StyledFormControl variant="outlined">
        <StyledInput
          value={value}
          onChange={onChange}
          onClick={onClick}
          onBlur={onBlur}
          name={name}
          ref={ref}
          variant="outlined"
          id={`formatted-text-mask-input-${id}`}
          inputComponent={MaskedNumber}
          placeholder={placeholder}
          endAdornment={
            <StyledInputAdornment position="end">{unit}</StyledInputAdornment>
          }
          aria-describedby={errorMessage ? `error-message-${id}` : undefined}
          aria-invalid={!!errorMessage}
        />
        {helperText && <div className="helper-text">{helperText}</div>}
        {errorMessage && (
          <div
            id={`error-message-${id}`}
            ref={errorRef}
            tabIndex={0}
            aria-live="polite"
            className="error-message"
          >
            {errorMessage}
          </div>
        )}
      </StyledFormControl>
    );
  }
);

MaskedInputWithUnits.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
